import { Controller } from '@hotwired/stimulus';
import { t } from '../javascript/lib/i18n';

// One-off "Improve with AI" for the report config form: sends the current main prompt
// and every non-removed section's title/question to the report `improve` endpoint, then
// fills the rewrites back in place (the user can still edit before saving). Fails
// quietly, leaving the user's own text untouched. Lives on the <form> so it can reach
// the prompt and all section rows, including ones added after connect.
export default class extends Controller {
  static targets = ['prompt', 'sectionRow', 'button', 'label'];
  static values = { url: String };

  async improve(event) {
    event.preventDefault();

    const rows = this.activeRows();
    const payload = {
      prompt: this.hasPromptTarget ? this.promptTarget.value : '',
      sections: rows.map((row) => ({
        title: this.fieldValue(row, 'title'),
        question: this.fieldValue(row, 'question'),
      })),
    };
    if (!payload.prompt.trim() && payload.sections.length === 0) return;

    this.setLoading(true);
    try {
      const response = await fetch(
        new URL(this.urlValue, window.location.href).toString(),
        { method: 'POST', headers: this.headers, body: JSON.stringify(payload) }
      );
      if (!response.ok) return;

      const json = await response.json();
      if (json.prompt && this.hasPromptTarget)
        this.setValue(this.promptTarget, json.prompt);
      (json.sections || []).forEach((section, index) => {
        const row = rows[index];
        if (!row) return;
        const title = this.field(row, 'title');
        const question = this.field(row, 'question');
        if (section.title && title) this.setValue(title, section.title);
        if (section.question && question)
          this.setValue(question, section.question);
      });
    } catch {
      // Network/parse error — leave the user's text as-is.
    } finally {
      this.setLoading(false);
    }
  }

  // Skip rows the user marked for removal (ai-report-sections#remove sets _destroy=1).
  activeRows() {
    return this.sectionRowTargets.filter((row) => {
      const destroy = row.querySelector(
        '[data-ai-report-sections-target="destroy"]'
      );
      return !(destroy && destroy.value === '1');
    });
  }

  field(row, name) {
    return row.querySelector(`[data-improve-field="${name}"]`);
  }

  fieldValue(row, name) {
    const el = this.field(row, name);
    return el ? el.value : '';
  }

  setValue(el, value) {
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  setLoading(loading) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = loading;
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = loading
        ? t('js.ai_report_improve.improving')
        : t('js.ai_report_improve.improve_with_ai');
    }
  }

  get headers() {
    return {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
        ?.content,
      'Content-Type': 'application/json; charset=UTF-8',
      Accept: 'application/json',
    };
  }
}
